import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  MessageSquare,
  Trash2,
  Pencil,
  Check,
  X,
  Loader2,
  Brain,
} from "lucide-react";
import {
  api,
  type SessionListItem,
  type ThinkingEffort,
} from "../lib/api.js";
import { cn } from "../lib/cn.js";

interface SessionPickerProps {
  currentSessionId: string | null;
  onSelect: (id: string | null) => void;
  /** When the picker opens with no current selection, the chat panel auto-creates
   *  a session on first send. We surface that here so the rail can show "New chat". */
  draftActive?: boolean;
}

const EFFORT_LABEL: Record<ThinkingEffort, string> = {
  low: "Low",
  medium: "Med",
  high: "High",
};

export function SessionPicker({
  currentSessionId,
  onSelect,
  draftActive,
}: SessionPickerProps) {
  const qc = useQueryClient();
  const sessionsQ = useQuery({
    queryKey: ["sessions"],
    queryFn: api.listSessions,
    refetchInterval: 15_000,
  });

  const deleteMut = useMutation({
    mutationFn: api.deleteSession,
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: ["sessions"] });
      if (currentSessionId === id) onSelect(null);
    },
  });

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-slate-200 px-2 py-2">
        <MessageSquare className="h-3.5 w-3.5 text-slate-500" />
        <span className="text-xs font-semibold text-slate-700">Sessions</span>
        <button
          onClick={() => onSelect(null)}
          title="Start a new chat"
          className="ml-auto inline-flex items-center gap-0.5 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[11px] text-slate-700 hover:bg-slate-50"
        >
          <Plus className="h-3 w-3" />
          New
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {sessionsQ.isLoading && (
          <div className="px-2 py-3 text-xs text-slate-400">
            <Loader2 className="inline h-3 w-3 animate-spin" /> loading…
          </div>
        )}

        {!currentSessionId && draftActive && (
          <SessionRow
            row={{
              id: "__draft__",
              title: "New chat",
              provider: "openai",
              model: "",
              thinkingEffort: "medium",
              sectionId: "",
              sectionName: "",
              notebookId: "",
              notebookName: "",
              siteId: "",
              createdAt: "",
              updatedAt: "",
              msgCount: 0,
              lastUserMsgPreview: null,
            }}
            selected
            isDraft
            onClick={() => onSelect(null)}
            onDelete={() => undefined}
            deleting={false}
          />
        )}

        {sessionsQ.data?.sessions.map((s) => (
          <SessionRow
            key={s.id}
            row={s}
            selected={currentSessionId === s.id}
            onClick={() => onSelect(s.id)}
            onDelete={() => {
              if (confirm(`Delete "${s.title}"?`)) deleteMut.mutate(s.id);
            }}
            deleting={deleteMut.isPending && deleteMut.variables === s.id}
          />
        ))}

        {!sessionsQ.isLoading &&
          sessionsQ.data?.sessions.length === 0 &&
          !draftActive && (
            <div className="px-2 py-4 text-[11px] italic text-slate-400">
              No sessions yet. Send a message to start one.
            </div>
          )}
      </div>
    </div>
  );
}

function SessionRow({
  row,
  selected,
  onClick,
  onDelete,
  deleting,
  isDraft,
}: {
  row: SessionListItem;
  selected: boolean;
  onClick: () => void;
  onDelete: () => void;
  deleting: boolean;
  isDraft?: boolean;
}) {
  const qc = useQueryClient();
  const [renaming, setRenaming] = useState(false);
  const [draftTitle, setDraftTitle] = useState(row.title);

  const renameMut = useMutation({
    mutationFn: (title: string) => api.patchSession(row.id, { title }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["sessions"] });
      void qc.invalidateQueries({ queryKey: ["session", row.id] });
      setRenaming(false);
    },
  });

  return (
    <div
      className={cn(
        "group border-b border-slate-100 px-2 py-1.5 last:border-b-0",
        selected ? "bg-slate-100" : "hover:bg-slate-50",
      )}
    >
      <div className="flex items-start gap-1">
        <button
          onClick={onClick}
          disabled={isDraft}
          className="min-w-0 flex-1 text-left disabled:cursor-default"
        >
          {renaming ? (
            <div className="flex items-center gap-1">
              <input
                value={draftTitle}
                onChange={(e) => setDraftTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") renameMut.mutate(draftTitle);
                  if (e.key === "Escape") setRenaming(false);
                }}
                autoFocus
                className="w-full rounded border border-slate-300 px-1 py-0.5 text-xs"
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  renameMut.mutate(draftTitle);
                }}
                className="text-emerald-600 hover:text-emerald-800"
              >
                <Check className="h-3 w-3" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setRenaming(false);
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ) : (
            <>
              <div className="truncate text-xs font-medium text-slate-800">
                {row.title}
              </div>
              {row.lastUserMsgPreview && (
                <div className="mt-0.5 truncate text-[10px] text-slate-500">
                  {row.lastUserMsgPreview}
                </div>
              )}
              <div className="mt-0.5 flex items-center gap-1.5 text-[10px] text-slate-400">
                {!isDraft && (
                  <>
                    <span>{row.msgCount} msg{row.msgCount === 1 ? "" : "s"}</span>
                    <span>·</span>
                    <span>{relTime(row.updatedAt)}</span>
                    <span className="ml-auto inline-flex items-center gap-0.5">
                      <Brain className="h-2.5 w-2.5" />
                      {EFFORT_LABEL[row.thinkingEffort]}
                    </span>
                  </>
                )}
              </div>
            </>
          )}
        </button>
        {!renaming && !isDraft && (
          <div className="flex flex-col items-center gap-0.5 opacity-0 group-hover:opacity-100">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setDraftTitle(row.title);
                setRenaming(true);
              }}
              className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              title="Rename"
            >
              <Pencil className="h-3 w-3" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              disabled={deleting}
              className="rounded p-0.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
              title="Delete"
            >
              {deleting ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Trash2 className="h-3 w-3" />
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function relTime(iso: string): string {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const m = Math.round(diff / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.round(h / 24);
  return `${d}d`;
}
